import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ENTITIES } from '../data';

type EntityId = keyof typeof ENTITIES;

interface Guide {
  title: string;
  text: string;
  terms: [EntityId, ...EntityId[]];
  path?: string;
}

export const GUIDES: Guide[] = [
  {
    title: 'Work with the Revvly Assistant',
    text: 'Ask the in-product AI about your clients, subscriptions, invoices, and revenue — and have it draft new catalog and subscriptions for you, with a confirmation card before anything changes.',
    terms: ['subscription', 'invoice', 'client'],
    path: '/docs/assistant',
  },
  {
    title: 'Connect Claude with MCP',
    text: 'Point Claude Desktop, Claude Code, or any MCP client at Revvly. Sign in with OAuth or send an API key, then ask about live billing data for one workspace — and propose writes that a signed-in human must approve in Revvly.',
    terms: ['apiKey', 'subscription', 'invoice'],
    path: '/docs/api/mcp',
  },
  {
    title: 'Run your sales pipeline',
    text: 'Capture prospects — or import people and companies from a spreadsheet — raise deals, and work them across a drag-and-drop board, then convert a won deal into a billing Client. Notes, documents, emails, meetings, and tasks all land on one activity timeline.',
    terms: ['lead', 'pipeline', 'client'],
    path: '/docs/guides/crm',
  },
  {
    title: 'Balance client work across your CSMs',
    text: 'See how many clients each customer-success manager looks after, split by status, and which clients have no manager. Work through the unassigned queue and move accounts between managers in one step; only the manager changes. Open to customer-success team leads and workspace admins.',
    terms: ['client'],
    path: '/docs/guides/csm-workload',
  },
  {
    title: 'Add custom fields and tags',
    text: 'Record what Revvly does not ask for on its own, such as an account tier or a renewal owner. Admins define fields and tags once; everyone with CRM fills them in on the client, contact or deal. Covers the nine field types, who can see a field, where fields show up (forms, columns, filters, search, import, email and the API) and how to retire one.',
    terms: ['client', 'lead'],
    path: '/docs/guides/custom-fields',
  },
  {
    title: 'Reuse documents with the Document library',
    text: 'Upload proposals, decks, and contracts once, keep them current with versions, attach small files to an email, and send large ones as tracked links placed in your text, with a notification when they are opened. Files up to 100 MB are type-checked, and malware-scanned where scanning is set up, before anyone can use them; only admins change what your team sends.',
    terms: ['crmActivity', 'client'],
    path: '/docs/guides/document-library',
  },
  {
    title: 'Collect answers with Forms',
    text: 'Build a contact, demo-request or application form, share its link, embed it or build it into your own site, and have each submission create a lead, email your team, reply to the person and post to Slack. Covers publishing, submissions, CSV export and spam limits.',
    terms: ['lead', 'pipeline'],
    path: '/docs/guides/forms',
  },
  {
    title: 'Work your inbox',
    text: 'Your own sorted view of your Gmail: Primary, Needs reply, Other, Snoozed and Done, with the client beside each thread. Teach it where a sender belongs, snooze, reply and clear newsletters in one go, and set a daily digest. Nobody else sees it, admins included.',
    terms: ['crmActivity', 'client'],
    path: '/docs/guides/work-inbox',
  },
  {
    title: 'Keep client folders in Google Drive',
    text: 'Give each client and prospect its own Drive folder, file invoices, uploads and attachments there, bring files people add in Drive back to the client, and mark invoices in a folder for import.',
    terms: ['client', 'invoice'],
    path: '/docs/guides/drive-folders',
  },
  {
    title: 'Run your OKRs',
    text: 'Plan a quarterly cycle, write objectives and Key Results, and let revenue Key Results read themselves from your billing data. Check in weekly in the app or in Slack, get a daily reminder while one is owed, link the ClickUp tasks behind each Key Result, then score and close.',
    terms: ['subscription', 'invoice', 'lifecycleEvent'],
    path: '/docs/guides/okrs',
  },
  {
    title: 'Test your bets with hypotheses',
    text: 'Put the plan under each Key Result: write each bet down, rank it on reach, impact, confidence and effort, see how much of the gap the plan could cover, state the test before running it, and record the verdict in the app or in Slack. Learnings carry from cycle to cycle.',
    terms: ['subscription'],
    path: '/docs/guides/hypotheses',
  },
  {
    title: 'Run daily check-ins in Slack',
    text: 'Ask your team a few questions in Slack on working days, at one time or on each person’s own clock, post each answer to a channel as it comes in, and close the day with a summary of who answered, skipped or was away.',
    terms: ['tenant'],
    path: '/docs/guides/daily-checkins',
  },
  {
    title: 'See how clients use your product',
    text: 'Read what each client does in your product beside what they pay: who is active, which features they use, how long a visit lasts and how clients compare. Organise the event names your product sends, let the documentation suggest what they mean, set up browser tracking, and erase a person’s events. Open to people with the Product usage feature; admins organise events.',
    terms: ['client', 'subscription'],
    path: '/docs/guides/product-usage',
  },
  {
    title: 'Send form prospects to Revvly',
    text: 'Map a form webhook to one public GraphQL mutation that creates a prospect, contact, and pipeline lead. Includes safe retry and validation examples.',
    terms: ['lead', 'client'],
    path: '/docs/guides/prospect-form-integration',
  },
  {
    title: 'Configure integration API keys',
    text: 'Set up credentials, scopes, signing secrets, and safe rotation for GraphQL, usage, event, and payment integrations.',
    terms: ['apiKey'],
    path: '/docs/guides/api-keys',
  },
  {
    title: 'Report usage through REST',
    text: 'Apply usage deltas, absolute counts, commission events, or batches with idempotent server-side requests.',
    terms: ['subscriptionUsage', 'apiKey'],
    path: '/docs/guides/usage-reporting',
  },
  {
    title: 'Capture metered usage',
    text: 'Close a metered period into a reading, seal it or waive it, and let the held invoice issue. Covers reading states, what an unreported period means per component, and the operator surfaces for working the queue.',
    terms: ['subscriptionUsage', 'invoice', 'variableComponent'],
    path: '/docs/guides/metered-usage',
  },
  {
    title: 'Integrate with public GraphQL',
    text: 'Provision billing clients, read invoices and receivables, and choose the right mutation for clients versus prospects.',
    terms: ['client', 'invoice', 'apiKey'],
    path: '/docs/guides/public-graphql',
  },
  {
    title: 'Work the support inbox',
    text: 'The views, filters and search, replying and internal notes with @mentions, taking and assigning, closing with a resolution and reopening, auto-close, attachments, a client’s tickets on the client page, and what each number on the Metrics page means.',
    terms: ['client', 'tenant'],
    path: '/docs/guides/support-inbox',
  },
  {
    title: 'Build the knowledge base the AI answers from',
    text: 'Add a help site, a document, or an Intercom or Zendesk help centre, say which roles and plans may read each article, clear the review queue, and keep sources healthy with re-reads, retries and the attention banner. Includes which source to choose.',
    terms: ['tenant'],
    path: '/docs/guides/support-knowledge',
  },
  {
    title: 'How the AI first line works',
    text: 'How the support AI answers with cited sources, who sees which article, when it hands over to a person, its guardrails, the per-type and workspace switches and spending cap, what counts as a billable resolution, how to practise in the sandbox, and how to compare it with Intercom’s Fin on your past conversations.',
    terms: ['tenant'],
    path: '/docs/guides/support-ai',
  },
  {
    title: 'Write guidelines for the support AI',
    text: 'Tell the support AI how to answer: the tone for each kind of customer, context your help articles leave out, when to ask a clarifying question, and which questions to leave to your team. What guidelines are, when to use each type, and how to add, scope, pause and test them.',
    terms: ['tenant'],
    path: '/docs/guides/support-guidelines',
  },
  {
    title: 'Set up Support for your team',
    text: 'Support roles, ticket types and workflows, teams, agent and customer notifications, client updates and ClickUp task linking, webhooks, and a go-live checklist.',
    terms: ['tenant', 'apiKey'],
    path: '/docs/guides/support-setup',
  },
  {
    title: 'Set up product event tracking',
    text: 'From nothing to each client\'s usage on their Revvly page: switch Product usage on, issue a key, set client External IDs, send events from your backend, and optionally add the browser script with a token route and allowed sites. Includes a prompt you can hand to an integration agent.',
    terms: ['apiKey', 'client'],
    path: '/docs/guides/product-events-setup',
  },
  {
    title: 'Add a support chat to your app',
    text: 'Step by step from nothing to a working chat in your product: the settings to turn on, one backend token route, a complete chat script, testing and a go-live checklist. Includes a prompt you can hand to an integration agent.',
    terms: ['apiKey', 'client'],
    path: '/docs/guides/support-widget',
  },
  {
    title: 'Sign users in to the support chat',
    text: 'Mint a short-lived token from your backend for the user you have signed in, so your widget or app can chat with your support team as that user. Covers secret keys, claims and roles, refresh, revocation and verifying tokens yourself.',
    terms: ['apiKey', 'client'],
    path: '/docs/guides/support-tokens',
  },
  {
    title: 'Push help articles to Support',
    text: 'Send the help articles that live in your own system to Revvly Support so the AI answers from them. Covers the knowledge key, adding and updating articles by your own id, stating who may read each one, removing articles, and keeping a source in step with a full refresh.',
    terms: ['apiKey'],
    path: '/docs/guides/support-knowledge-api',
  },
  {
    title: 'Add a team member',
    text: 'Invite a colleague into your workspace by email, choose their role, and let them set their own password. Manage roles and remove access from the same screen.',
    terms: ['tenant'],
    path: '/docs/guides/add-user',
  },
  {
    title: 'Customize your invoices',
    text: 'Put your logo, company and tax details, banking and payment information, payment terms, and currency on every invoice — and see how Revvly numbers them automatically.',
    terms: ['invoice', 'tenant'],
    path: '/docs/guides/customize-invoices',
  },
  {
    title: 'Choose a pricing model',
    text: 'Flat, volume-tiered, graduated, stairstep, true overage, committed overage, commission, promotional — when to reach for each, with worked examples.',
    terms: ['variableComponent'],
    path: '/docs/guides/pricing-models',
  },
  {
    title: 'Price a component per scope',
    text: 'Global default, bundle override, module override, single-subscription override. How Revvly resolves which price wins at billing time.',
    terms: ['variableComponentPrice', 'bundle', 'subscription'],
    path: '/docs/guides/scoped-pricing',
  },
  {
    title: 'Negotiate a private deal',
    text: 'Write a Custom Pricing Agreement for one customer on one component. When it takes effect, what it overrides, how to revoke it.',
    terms: ['customPricingAgreement', 'subscription'],
    path: '/docs/guides/custom-agreements',
  },
  {
    title: 'Override base or component pricing',
    text: 'Replace the catalog-calculated base price, or write a subscription-scoped component rate, with an immediate-prorated or next-renewal effective date. No catalog guardrails — use a discount instead if you want a bounded, reversible reduction.',
    terms: ['subscription', 'variableComponentPrice'],
    path: '/docs/guides/override-pricing',
  },
  {
    title: 'Set advance vs. arrears billing',
    text: 'Choose whether a component invoices at the start of the period or the end, once usage is known. Override per product, per subscription, or per Custom Pricing Agreement — changes are always prospective.',
    terms: ['variableComponent', 'subscription'],
    path: '/docs/guides/billing-timing',
  },
  {
    title: 'Apply a flexible discount',
    text: 'Build a reusable discount template or a one-off, attach it to a base price or a component, choose how long it lasts and how it stacks with others, and preview the next invoice before saving.',
    terms: ['discount', 'subscription'],
    path: '/docs/guides/discounting',
  },
  {
    title: 'Version, upgrade, grandfather',
    text: 'How Module and Bundle versions work, how Subscriptions pin to them, when to upgrade vs. grandfather existing customers.',
    terms: ['module', 'bundle', 'subscription'],
    path: '/docs/guides/versioning',
  },
  {
    title: 'Price a client on an older version',
    text: 'Create the version of a product as it was — last year\u2019s prices, an older module set — and put a client on it. For customers who predate Revvly, or who keep the plan you were selling at the time.',
    terms: ['bundle', 'module', 'subscription'],
    path: '/docs/guides/legacy-pricing',
  },
  {
    title: 'Launch a new Bundle',
    text: 'Add a versioned package to your catalog, pin its modules, ship a per-currency price, run the publish-gap check, and make it available to customers.',
    terms: ['bundle', 'variableComponentPrice', 'region'],
    path: '/docs/guides/launch-bundle',
  },
  {
    title: 'Handle a mid-cycle change',
    text: 'Migration, plan change, or expansion — how to tell them apart, preview the proration, and choose between applying now or at the renewal boundary.',
    terms: ['expansion', 'subscription'],
    path: '/docs/guides/mid-cycle-changes',
  },
  {
    title: 'Bill a prepaid term or a deferred go-live',
    text: 'Sell a year upfront on one invoice, or sign months before the customer starts. What the span pays ahead and what still bills each period, where the prepay incentive applies, what activation does to the term, and what cancellation owes.',
    terms: ['subscription', 'invoice'],
    path: '/docs/guides/billing-schedules',
  },
  {
    title: 'Sell a committed term',
    text: 'Record the contract a subscription is sold under — an annual commitment billed monthly, what happens when the term ends, and what leaving early costs. Attach it, roll it, or end it.',
    terms: ['contract', 'subscription', 'client'],
    path: '/docs/guides/contracts',
  },
  {
    title: 'Give money back with a credit note',
    text: 'Raise a numbered credit against an invoice, then either spend it on an open receivable or record the cash refund. What each state does to receivables and collected cash, and the two paths that raise one for you.',
    terms: ['invoice', 'client'],
    path: '/docs/guides/credit-notes',
  },
  {
    title: 'Run the renewal cycle',
    text: 'Calendar, approval queue, bulk decisions, dry-run preview — and exactly what Revvly applies at the renewal boundary: pending migrations, agreements, discount roll-off.',
    terms: ['subscription', 'lifecycleEvent'],
    path: '/docs/guides/renewals',
  },
  {
    title: 'Set up multi-currency billing',
    text: 'Sell in local currencies while keeping a single reporting currency. Exchange rates update daily.',
    terms: ['region', 'exchangeRate', 'subscription'],
    path: '/docs/guides/multi-currency',
  },
  {
    title: 'Ingest usage events',
    text: 'Stream metered usage from your application. HMAC signing, scoped API keys, deduplication, real-time counters.',
    terms: ['apiKey', 'subscriptionUsage'],
    path: '/docs/api/events',
  },
  {
    title: 'Mark invoices paid from your payment system',
    text: 'Post a payment the moment your processor or bank feed sees it — in full, or a part payment that leaves the rest outstanding and still chasing.',
    terms: ['invoice', 'apiKey'],
    path: '/docs/guides/webhook-payment',
  },
  {
    title: 'Reference resources by your own id',
    text: 'Set externalId at creation and report usage with externalSubscriptionId + variableSlug — never store a Revvly id. Plus the GET discovery endpoints for subscriptions and variables.',
    terms: ['subscription', 'apiKey', 'meter'],
    path: '/docs/guides/external-id',
  },
  {
    title: 'Read the lifecycle event log',
    text: 'Every revenue-moving moment is an immutable event with native + reporting deltas. Here is how to query, filter, and reconcile the log.',
    terms: ['lifecycleEvent'],
    path: '/docs/guides/lifecycle-log',
  },
  {
    title: 'Import historical invoices',
    text: 'Bring billing history in from Excel or Google Drive, then rebuild the subscriptions those invoices imply so analytics reflect the full picture from day one.',
    terms: ['invoice'],
    path: '/docs/guides/import-invoices',
  },
  {
    title: 'Collect what you are owed',
    text: 'Read receivables and the cash-flow chart, send invoices and reminders, record partial payments, reopen a paid invoice and send from a shared accounting mailbox.',
    terms: ['invoice', 'client'],
    path: '/docs/guides/collect-payments',
  },
  {
    title: 'Merge, delete and undo',
    text: 'Merge duplicate clients, delete clients and subscriptions, roll back an import batch, import invoice lines from a spreadsheet and resolve invoice discrepancies.',
    terms: ['client', 'subscription', 'invoice'],
    path: '/docs/guides/fix-your-data',
  },
  {
    title: 'Work your tasks',
    text: 'Create tasks, read them as a table or board, clear the day in the Work queue, and create or close tasks as you send email. States plainly what a task reminder delivers today.',
    terms: ['lead', 'client', 'contact'],
    path: '/docs/guides/tasks',
  },
  {
    title: 'Configure your workspace',
    text: 'Every Org Settings tab in one line, then reporting currency, payment terms, working days, invoice header, AI spend, catalog learning, API keys and integrations in detail.',
    terms: ['tenant', 'featureFlag', 'apiKey'],
    path: '/docs/guides/workspace-settings',
  },
  {
    title: 'Collect tax on invoices',
    text: "Register where you are liable, publish rates, describe each client's tax position and let Revvly add tax when invoices are issued. Covers the tax report and the ledger.",
    terms: ['invoice', 'client'],
    path: '/docs/guides/tax',
  },
  {
    title: 'Create a subscription',
    text: 'The four-step wizard from client and plan to first invoice, including backdated starts, excluded meters, custom prices and start-date regeneration.',
    terms: ['subscription', 'client', 'invoice'],
    path: '/docs/guides/create-subscription',
  },
  {
    title: 'Set up teams, seats and invites',
    text: 'Invite people, watch your seat limit and ask for more, change roles, and group people into teams that OKRs and CRM pipelines can use.',
    terms: ['tenant', 'pipeline'],
    path: '/docs/guides/teams-and-seats',
  },
  {
    title: 'Get notified: the bell, push and Slack',
    text: 'What puts something in the bell, how to choose what reaches you, install the app for push, and connect Slack or ClickUp Chat for OKR check-ins.',
    terms: ['tenant'],
    path: '/docs/guides/notifications',
  },
  {
    title: 'Send events out with webhooks',
    text: 'Post CRM lead changes to Zapier or your own server, and Support ticket events to your backend, with signatures you can verify, retries and a delivery log.',
    terms: ['lead', 'pipeline', 'apiKey'],
    path: '/docs/guides/outbound-webhooks',
  },
];

/**
 * How the guides index is grouped. Sections are keyed on guide path rather than
 * a field on each GUIDES entry because scripts/generate-llms-txt.js parses that
 * array with a fixed-shape regex. A guide missing from every section still
 * shows, under "More guides", so adding a guide never hides it.
 */
const SECTIONS: { id: string; label: string; blurb: string; paths: string[] }[] = [
  {
    id: 'workspace',
    label: 'Set up your workspace',
    blurb: 'Settings, people, seats, notifications and your own task list.',
    paths: [
      '/docs/guides/workspace-settings',
      '/docs/guides/add-user',
      '/docs/guides/teams-and-seats',
      '/docs/guides/notifications',
      '/docs/guides/tasks',
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog and pricing',
    blurb: 'Decide what you sell and how it is priced, per market, per client and over time.',
    paths: [
      '/docs/guides/pricing-models',
      '/docs/guides/launch-bundle',
      '/docs/guides/scoped-pricing',
      '/docs/guides/multi-currency',
      '/docs/guides/billing-timing',
      '/docs/guides/discounting',
      '/docs/guides/override-pricing',
      '/docs/guides/custom-agreements',
      '/docs/guides/versioning',
      '/docs/guides/legacy-pricing',
    ],
  },
  {
    id: 'billing',
    label: 'Subscriptions and invoices',
    blurb: 'Create subscriptions, change and renew them, invoice, collect and correct.',
    paths: [
      '/docs/guides/create-subscription',
      '/docs/guides/mid-cycle-changes',
      '/docs/guides/billing-schedules',
      '/docs/guides/contracts',
      '/docs/guides/renewals',
      '/docs/guides/metered-usage',
      '/docs/guides/customize-invoices',
      '/docs/guides/tax',
      '/docs/guides/collect-payments',
      '/docs/guides/credit-notes',
      '/docs/guides/import-invoices',
      '/docs/guides/lifecycle-log',
      '/docs/guides/fix-your-data',
    ],
  },
  {
    id: 'sales',
    label: 'Sales and client work',
    blurb: 'The pipeline, your inbox, documents, forms and client folders.',
    paths: [
      '/docs/guides/crm',
      '/docs/guides/csm-workload',
      '/docs/guides/work-inbox',
      '/docs/guides/document-library',
      '/docs/guides/forms',
      '/docs/guides/drive-folders',
      '/docs/guides/product-usage',
    ],
  },
  {
    id: 'support',
    label: 'Support',
    blurb:
      'Run the support inbox, the knowledge base, the AI first line and its guidelines, and add chat to your app.',
    paths: [
      '/docs/guides/support-setup',
      '/docs/guides/support-inbox',
      '/docs/guides/support-knowledge',
      '/docs/guides/support-ai',
      '/docs/guides/support-guidelines',
      '/docs/guides/support-widget',
      '/docs/guides/support-tokens',
      '/docs/guides/support-knowledge-api',
    ],
  },
  {
    id: 'goals',
    label: 'Goals and team rhythm',
    blurb: 'OKRs, the bets behind them and the daily check-in.',
    paths: ['/docs/guides/okrs', '/docs/guides/hypotheses', '/docs/guides/daily-checkins'],
  },
  {
    id: 'assistant',
    label: 'The Revvly Assistant',
    blurb: 'Ask in plain words, in the app or from Claude.',
    paths: ['/docs/assistant', '/docs/api/mcp'],
  },
  {
    id: 'developers',
    label: 'Integrate with your systems',
    blurb: 'API keys, the public API, usage, payments and webhooks.',
    paths: [
      '/docs/guides/api-keys',
      '/docs/guides/public-graphql',
      '/docs/guides/usage-reporting',
      '/docs/api/events',
      '/docs/guides/product-events-setup',
      '/docs/guides/webhook-payment',
      '/docs/guides/outbound-webhooks',
      '/docs/guides/external-id',
      '/docs/guides/prospect-form-integration',
    ],
  },
];

type GuideItem = (typeof GUIDES)[number];

function groupGuides(): { id: string; label: string; blurb: string; guides: GuideItem[] }[] {
  const byPath = new Map(GUIDES.map((g) => [g.path, g]));
  const placed = new Set<string>();
  const groups = SECTIONS.map(({ paths, ...rest }) => ({
    ...rest,
    guides: paths.flatMap((p) => {
      const g = byPath.get(p);
      if (!g) return [];
      placed.add(p);
      return [g];
    }),
  })).filter((s) => s.guides.length);
  const rest = GUIDES.filter((g) => !g.path || !placed.has(g.path));
  if (rest.length) {
    groups.push({ id: 'more', label: 'More guides', blurb: 'Everything else.', guides: rest });
  }
  return groups;
}

export function Guides() {
  const groups = groupGuides();
  useEffect(() => {
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) {
      target.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    document.title = 'How-To Guides — Revvly Subscription Revenue Management';
  }, []);
  return (
    <div className="tn-page">
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guides</span>
        <h1>
          How-to flows for <em>common</em> work.
        </h1>
        <p className="dp-oneliner">
          Step-by-step walkthroughs for the things you&rsquo;ll do most often. Each guide
          cross-references the concepts it touches.
        </p>
      </header>
      <nav className="tn-guide-jump" aria-label="Guide sections">
        {groups.map((sec) => (
          <a key={sec.id} href={`#${sec.id}`} className="tn-guide-jump-link">
            {sec.label}
            <span className="tn-guide-jump-count">{sec.guides.length}</span>
          </a>
        ))}
      </nav>
      {groups.map((sec) => (
        <section key={sec.id} id={sec.id} className="tn-guide-section">
          <h2>{sec.label}</h2>
          <p className="tn-guide-section-sub">{sec.blurb}</p>
          <div className="tn-guide-grid">
            {sec.guides.map((g) => (
              <Link
                key={g.path ?? g.title}
                className="tn-guide-card"
                to={g.path ?? `/docs/concepts/${g.terms[0]}`}
              >
                <h3>{g.title}</h3>
                <p>{g.text}</p>
                <div className="tn-guide-tags">
                  {g.terms.map((t) => (
                    <span key={t} className="tn-guide-tag">
                      {ENTITIES[t]?.name || t}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
